import { RouteManagerContext, useIonRouter } from '@ionic/react';
import { type ComponentType, useContext, useEffect, useRef } from 'react';

import { AppIonPage } from '@/core/components/app-ion-page';
import { persistAuthReturnUrl } from '@/core/router/auth-route-gate';
import {
  enterAppShell,
  isRouteManagerReady,
} from '@/core/router/enter-app-shell';
import { shellPaths } from '@/core/router/app-paths';
import { AuthStatus, useAuthStore } from '@/core/store/auth.store';

interface WithAuthOptions {
  redirectTo?: string;
  redirectIfAuthenticated?: boolean;
  redirectAuthenticatedTo?: string;
}

export function withAuth<P extends object>(
  Component: ComponentType<P>,
  options: WithAuthOptions = {},
): ComponentType<P> {
  const {
    redirectTo,
    redirectIfAuthenticated = false,
    redirectAuthenticatedTo,
  } = options;

  return function AuthenticatedComponent(props: P) {
    const router = useIonRouter();
    const routeManager = useContext(RouteManagerContext);
    const authState = useAuthStore();
    const isAuthenticated =
      authState.authStatus === AuthStatus.AUTHENTICATED ||
      authState.authStatus === AuthStatus.OFFLINE;
    const isUnauthenticated =
      authState.authStatus === AuthStatus.UNAUTHENTICATED;

    const lastRedirectedStatus = useRef<AuthStatus | null>(null);

    useEffect(() => {
      if (!authState.hydrated) {
        return;
      }

      if (redirectIfAuthenticated && isAuthenticated) {
        if (lastRedirectedStatus.current !== authState.authStatus) {
          lastRedirectedStatus.current = authState.authStatus;
          const target = redirectAuthenticatedTo ?? shellPaths.home;
          enterAppShell(
            router,
            isRouteManagerReady(routeManager) ? routeManager : undefined,
            target,
          );
        }
        return;
      }

      if (!redirectIfAuthenticated && isUnauthenticated) {
        if (lastRedirectedStatus.current !== AuthStatus.UNAUTHENTICATED) {
          lastRedirectedStatus.current = AuthStatus.UNAUTHENTICATED;
          if (typeof window !== 'undefined') {
            persistAuthReturnUrl(
              window.location.pathname + window.location.search,
            );
          }
          const target = redirectTo ?? '/auth';
          router.push(target, 'root', 'replace');
        }
        return;
      }

      // Auth state is now valid for this page -- clear the guard so a future
      // transition (e.g. token expiry) can trigger a new redirect.
      lastRedirectedStatus.current = null;
    }, [
      authState.hydrated,
      authState.authStatus,
      isAuthenticated,
      isUnauthenticated,
      router,
      routeManager,
    ]);

    // One stable AppIonPage for the IonRoute lifetime. Never swap an empty
    // IonPage for the page's IonPage after hydration — that sticks
    // `ion-page-invisible` after animated pushes.
    return (
      <AppIonPage>
        {authState.hydrated ? <Component {...props} /> : null}
      </AppIonPage>
    );
  };
}
